import "./styles.scss";
import * as React from "react";
const ResponsiveBar = React.lazy(() => import("@nivo/bar").then(m => ({ default: m.ResponsiveBar })));

interface TimeChartProps {
  dailySummary: any[];
  start: Date;
  end: Date;
  metricType: string;
}

const TimeChart: React.FC<TimeChartProps> = ({ dailySummary, start, end, metricType }) => {
  const defaultEndDate = new Date();
  defaultEndDate.setHours(23, 59, 59, 999);
  const defaultStartDate = new Date(defaultEndDate);
  defaultStartDate.setDate(1);
  defaultStartDate.setHours(0, 0, 0, 0);

  const getDataGroupedByDay = (dailySummary: any[], start: Date, end: Date) => {
    const data = [];
    for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
      const id = `${date.toLocaleString("default", { month: "short" })} ${date.getDate()}`;

      const record = dailySummary.find(
        r => r.year === date.getFullYear() && r.month === date.getMonth() + 1 && r.day === date.getDate()
      );

      data.push({
        id,
        label: `${date.toLocaleString("default", { month: "short" })} ${date.getDate()}, ${date.getFullYear()}`,
        value: record?.total || 0,
      });
    }
    return data;
  };

  const getDataGroupedByWeek = (dailySummary: any[], start: Date, end: Date) => {
    const data = [];
    let weekStart = new Date(start);
    const daysSinceMonday = weekStart.getDay() === 0 ? 6 : weekStart.getDay() - 1;
    weekStart.setDate(weekStart.getDate() - daysSinceMonday);

    while (weekStart <= end) {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);

      let value = 0;
      for (let d = new Date(weekStart); d <= weekEnd; d.setDate(d.getDate() + 1)) {
        const record = dailySummary.find(
          r => r.year === d.getFullYear() && r.month === d.getMonth() + 1 && r.day === d.getDate()
        );
        value += record?.total || 0;
      }

      var id = weekStart.toLocaleString("default", { month: "short", day: "numeric", year: "2-digit" });
      var label = `Week of ${weekStart.toLocaleString("default", {
        month: "short",
        day: "numeric",
      })}, ${weekStart.getFullYear()}`;
      if (weekStart < start) {
        id = start.toLocaleString("default", { month: "short", day: "numeric", year: "2-digit" });
        label = `${start.toLocaleString("default", { month: "short", day: "numeric" })} to ${weekEnd.toLocaleString(
          "default",
          { month: "short", day: "numeric" }
        )}, ${weekEnd.getFullYear()}`;
      } else if (weekEnd > end) {
        label = `${weekStart.toLocaleString("default", { month: "short", day: "numeric" })} to ${end.toLocaleString(
          "default",
          { month: "short", day: "numeric" }
        )}, ${end.getFullYear()}`;
      }

      data.push({
        id,
        label,
        value,
      });

      weekStart.setDate(weekStart.getDate() + 7);
    }

    return data;
  };

  const getDataGroupedByMonth = (dailySummary: any[], start: Date, end: Date) => {
    const data = [];
    let current = new Date(start);
    while (current <= end) {
      const monthStart = new Date(current.getFullYear(), current.getMonth(), 1);
      const monthEnd = new Date(current.getFullYear(), current.getMonth() + 1, 0, 23, 59, 59, 999);

      let value = 0;
      for (let d = new Date(monthStart); d <= monthEnd; d.setDate(d.getDate() + 1)) {
        const record = dailySummary.find(
          r => r.year === d.getFullYear() && r.month === d.getMonth() + 1 && r.day === d.getDate()
        );
        value += record?.total || 0;
      }

      const id = monthStart.toLocaleString("default", { month: "short", year: "numeric" });
      var label: string;
      if (monthStart < start) {
        label = `${start.toLocaleString("default", { month: "short", day: "numeric" })} to ${monthEnd.toLocaleString(
          "default",
          { month: "short", day: "numeric" }
        )}, ${monthStart.getFullYear()}`;
      } else if (monthEnd > end) {
        label = `${monthStart.toLocaleString("default", { month: "short", day: "numeric" })} to ${end.toLocaleString(
          "default",
          { month: "short", day: "numeric" }
        )}, ${end.getFullYear()}`;
      } else {
        label = `${monthStart.toLocaleString("default", { month: "long" })}, ${monthStart.getFullYear()}`;
      }

      data.push({
        id,
        label,
        value,
      });

      current.setMonth(current.getMonth() + 1, 1);
      current.setHours(0, 0, 0, 0);
    }
    return data;
  };

  const getDataGroupedByYear = (dailySummary: any[], start: Date, end: Date) => {
    const data = [];
    let currentYear = start.getFullYear();
    const endYear = end.getFullYear();
    while (currentYear <= endYear) {
      const yearStart = new Date(currentYear, 0, 1);
      const yearEnd = new Date(currentYear, 11, 31, 23, 59, 59, 999);

      const value = dailySummary.filter(r => r.year === currentYear).reduce((sum, r) => sum + (r.total || 0), 0);

      const id = currentYear.toString();
      var label: string;
      if (yearStart < start) {
        label = `${start.toLocaleString("default", { month: "short", day: "numeric" })} to ${yearEnd.toLocaleString(
          "default",
          { month: "short", day: "numeric" }
        )}, ${yearStart.getFullYear()}`;
      } else if (yearEnd > end) {
        label = `${yearStart.toLocaleString("default", { month: "short", day: "numeric" })} to ${end.toLocaleString(
          "default",
          { month: "short", day: "numeric" }
        )}, ${end.getFullYear()}`;
      } else {
        label = id;
      }

      data.push({
        id,
        label,
        value,
      });

      currentYear++;
    }
    return data;
  };

  const totalDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

  var data: any[];
  if (totalDays <= 60) {
    data = getDataGroupedByDay(dailySummary, start, end);
  } else if (totalDays <= 365) {
    data = getDataGroupedByWeek(dailySummary, start, end);
  } else if (totalDays <= 60 * 30) {
    data = getDataGroupedByMonth(dailySummary, start, end);
  } else {
    data = getDataGroupedByYear(dailySummary, start, end);
  }

  return (
    <div className="chart">
      <div className="chartSvg">
        <ResponsiveBar
          data={data}
          margin={{ top: 10, right: 0, bottom: 60, left: 50 }}
          axisBottom={{ tickPadding: 5, tickRotation: -60 }}
          axisLeft={{ tickPadding: 5, legend: `${metricType}s`, legendOffset: -40 }}
          labelSkipHeight={3}
          tooltipLabel={el => `${el.data.label}`}
        />
      </div>
    </div>
  );
};
export default TimeChart;
